// @ts-strict-ignore
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AggregationEditorContext } from './context';
import AggregationsSubEditorContainer from './AggregationsSubEditorContainer';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import { selectExpressionActiveSubEditor, selectWorkflowData, selectWorkflowProps } from 'flow/store/redux/selectors';
import { getAggregationConfigForAttribute } from 'flow/store/engine/update/helpers';
import { populateDataRouteData } from 'flow/service/dataRoutes/dataRoutes';
import type { AggregationSourceData } from './types';
import type { DataRoute } from 'flow/store/engine/types';
import {
  aggregationComponentsToSourceData,
  sourceDataToAggregationComponents,
} from './transformers';
import { getIsSaveDisabled } from './helpers';
import { resetExpressionSubEditor, updateDataRouteValue } from 'flow/store/redux/slice';
import { getMultiSelectProps } from '../NodeEditorDrawer/ThresholdsTab/RuleBuilderContainer/Rule/Threshold/helpers';
import { datadogRum } from '@datadog/browser-rum';
import { useKeyboardFocus, useSnackBar } from '@usealloy/component-library';
import { keyBinds } from '../NodeEditorDrawer/ThresholdsTab/RuleBuilderContainer/constants';
import { INACTIVE_AGGREGATION_OBJECT_MESSAGE } from './constants';
import { createWorkflowHistoryThunk } from 'flow/store/redux/thunks';
import { UserContext } from '~/dashboard/context';
import { changeDescription } from 'flow/store/redux/types';
import type { WorkflowHistoryContext } from 'flow/store/redux/types';
import { Operation } from 'flow/service/changelog/types';
import { createUiEffectContext } from 'flow/service/history';

interface AggregationDataRouteWrapperProps {
  dataRoute: DataRoute;
}

const AggregationDataRouteWrapper = ({ dataRoute }: AggregationDataRouteWrapperProps) => {
  const dispatch = useAppDispatch();
  const { actions } = useAppSelector(selectWorkflowData);
  const isolatedEditingBlock = useAppSelector(selectExpressionActiveSubEditor);

  const {
    services,
    aggregationConfig: aggregationConfigByObjectType,
    universalAttributes,
  } = useAppSelector(selectWorkflowProps);
  const { featureFlags } = useContext(UserContext);
  const addSnackBar = useSnackBar();

  const populatedDataRoute = useMemo(
    () => populateDataRouteData({
      actions, services, dataRoute, universalAttributes,
    }),
    [actions, services, dataRoute, universalAttributes],
  );

  const attribute = populatedDataRoute?.attribute?.details;

  const aggregationConfig = useMemo(
    () => (
      attribute
        ? getAggregationConfigForAttribute(attribute, aggregationConfigByObjectType)
        : undefined
    ),
    [attribute, aggregationConfigByObjectType],
  );

  const aggregationEditorContainerRef = useRef<HTMLDivElement>(null);
  const [sourceData, setSourceData] = useState<AggregationSourceData>(
    () => aggregationComponentsToSourceData(
      dataRoute.aggregationComponents, aggregationConfig?.objectType,
    ),
  );

  useEffect(() => {
    // hydrates the sub editor if something else outside the subeditor
    // changes the data route, like undo/redo
    setSourceData(aggregationComponentsToSourceData(
      dataRoute.aggregationComponents, aggregationConfig?.objectType,
    ));
  }, [dataRoute.aggregationComponents, aggregationConfig?.objectType]);

  const isSaveDisabled = useMemo(
    () => getIsSaveDisabled(sourceData),
    [sourceData],
  );

  const keyboardFocus = useKeyboardFocus({
    containerRef: aggregationEditorContainerRef,
    keyBinds,
    onError: (error, context) => datadogRum.addError(error, context),
  });

  const handleClose = useCallback(() => {
    dispatch(resetExpressionSubEditor());
  }, [dispatch]);

  const workflowHistoryContext: WorkflowHistoryContext = useMemo(() => ({
    operation: Operation.Modify,
    uiEffectContext: createUiEffectContext(changeDescription.aggregation, {
      actionId: populatedDataRoute.targetId,
      subEditorArgs: { dataRoute: isolatedEditingBlock },
    }),
  }), [populatedDataRoute.targetId, isolatedEditingBlock]);

  const handleSave = useCallback(() => {
    const existingComponents = populatedDataRoute.aggregationComponents;
    const withFallbacks = [{
      objectSlug: aggregationConfig?.objectType,
      objectVersionPublicVersion: aggregationConfig?.publicVersion,
      ...existingComponents[0],
    }, ...existingComponents.slice(1)];

    dispatch(
      createWorkflowHistoryThunk(
        () => dispatch(updateDataRouteValue({
          path: `${populatedDataRoute.publicId}.aggregationComponents`,
          value: sourceDataToAggregationComponents(sourceData, withFallbacks),
        })),
        Boolean(featureFlags?.workflowEditorUndoRedo),
        workflowHistoryContext,
      ),
    );

    handleClose();
  }, [
    dispatch,
    workflowHistoryContext,
    handleClose,
    sourceData,
    aggregationConfig,
    populatedDataRoute,
    featureFlags,
  ]);

  const aggregationEditorContext = useMemo(() => ({
    sourceData,
    setSourceData,
    handleSave,
    handleCancel: () => {
      setSourceData(aggregationComponentsToSourceData(
        populatedDataRoute.aggregationComponents, aggregationConfig?.objectType,
      ));
      handleClose();
    },
    aggregationEditorContainerRef,
    multiSelectProps: getMultiSelectProps({
      action: populatedDataRoute?.sourceAction,
      options: { isPreview: false },
    }),
    aggregationConfig,
    isSaveDisabled,
    validationErrors: {},
    keyboardFocus,
  }), [
    sourceData,
    populatedDataRoute,
    aggregationConfig,
    isSaveDisabled,
    keyboardFocus,
    handleClose,
    handleSave,
  ]);

  // The object's active version isn't selectable (e.g. an existing route on an
  // object that has since been deactivated). Close the editor and explain why
  // rather than rendering a broken/blank editor.
  useEffect(() => {
    if (!aggregationConfig) {
      addSnackBar({ title: INACTIVE_AGGREGATION_OBJECT_MESSAGE, type: 'danger' });
      handleClose();
    }
  }, [aggregationConfig, addSnackBar, handleClose]);

  if (!aggregationConfig) return null;

  return (
    <AggregationEditorContext.Provider value={aggregationEditorContext}>
      <AggregationsSubEditorContainer />
    </AggregationEditorContext.Provider>
  );
};

export default AggregationDataRouteWrapper;
